<script setup lang="ts">
  import * as changeCase from 'change-case';
  import { computed } from 'vue';

  import type { Call } from '@stuut/api/models';
  import { CallStatus, Direction } from '@stuut/api/models';
  import { formatDuration, formatPhoneNumber } from '@stuut/common/src/utils';
  import { Shimmer } from '@ui/components/ai-elements/shimmer';
  import { Badge } from '@ui/components/badge';
  import { Card, CardContent, CardHeader, CardTitle } from '@ui/components/card';
  import {
    PropertyList,
    PropertyListItem,
    PropertyListItemLabel,
    PropertyListItemValue,
  } from '@ui/components/property-list';
  import { Section, SectionContent, SectionHeader, SectionTitle } from '@ui/components/section';
  import { Separator } from '@ui/components/separator';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import InvoiceQuickView from '@/components/invoices/InvoiceQuickView.vue';
  import { PartnerQuickView } from '@/components/partners';
  import { useCallProcessingState } from '@/composables/useCallProcessingState';

  import CallSentimentIcon from './CallSentimentIcon.vue';
  import CallStatusBadge from './CallStatusBadge.vue';

  interface Props {
    call: Call;
  }

  const props = defineProps<Props>();

  const { isProcessing, processingLabel } = useCallProcessingState(() => props.call);

  const requestedBy = computed(() => {
    if (props.call.workflow?.name) {
      return `${props.call.workflow.name} Workflow`;
    }
    return props.call.approver?.name || 'AI Agent';
  });
</script>

<template>
  <Card>
    <CardHeader>
      <div class="flex items-center gap-x-3.5">
        <CardTitle>
          <Shimmer v-if="isProcessing" as="span" :duration="1.5"> Call Details </Shimmer>
          <template v-else>Call Details</template>
        </CardTitle>

        <Badge v-if="isProcessing && processingLabel" variant="outline" size="sm">
          <Shimmer as="span" :duration="1.5"> {{ processingLabel }} </Shimmer>
        </Badge>
      </div>

      <CallStatusBadge v-if="call.status" :status="call.status" />
    </CardHeader>

    <CardContent class="flex flex-col gap-4">
      <PropertyList>
        <PropertyListItem v-if="call.partner">
          <PropertyListItemLabel> Customer</PropertyListItemLabel>
          <PropertyListItemValue>
            <PartnerQuickView :partner="call.partner" />
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.invoices?.length">
          <PropertyListItemLabel> Invoices</PropertyListItemLabel>
          <PropertyListItemValue>
            <div class="flex flex-wrap gap-x-1">
              <InvoiceQuickView v-for="i in call.invoices" :key="i.id" :invoice="i" />
            </div>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> Date</PropertyListItemLabel>
          <PropertyListItemValue>
            <span>
              {{
                call.start_time
                  ? call.start_time.format('MMMM D, YYYY')
                  : call.created_at.format('MMMM D, YYYY')
              }}
              at
              {{
                call.start_time
                  ? call.start_time.local().format('h:mm A')
                  : call.created_at.local().format('h:mm A')
              }}
            </span>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> Direction</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ changeCase.capitalCase(call.direction) }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> From</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ formatPhoneNumber(call.from_number) }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> To</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ formatPhoneNumber(call.to_number) }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.transferred_to">
          <PropertyListItemLabel> Transferred To</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ formatPhoneNumber(call.transferred_to) }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.start_time">
          <PropertyListItemLabel> Duration</PropertyListItemLabel>
          <PropertyListItemValue>
            <span>
              {{
                call.start_time
                  ? call.status === CallStatus.COMPLETED
                    ? formatDuration(call.duration)
                    : '--'
                  : '--'
              }}
            </span>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem
          v-if="
            call.disconnection_reason && !call.disconnection_reason.toLowerCase().includes('hangup')
          "
        >
          <PropertyListItemLabel> Result</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ changeCase.capitalCase(call.disconnection_reason) }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> Classifications</PropertyListItemLabel>
          <PropertyListItemValue>
            <template v-if="call.classifications?.length">
              <span v-for="(ic, idx) in call.classifications" :key="idx">
                {{ changeCase.capitalCase(ic.classification) }}
                <template v-if="call.classifications.length !== idx + 1">, </template>
              </span>
            </template>

            <span v-else>--</span>
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.custom_classifications?.length">
          <PropertyListItemLabel> Custom Classifications</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ call.custom_classifications.join(', ') }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.sentiment">
          <PropertyListItemLabel> User Sentiment</PropertyListItemLabel>
          <PropertyListItemValue>
            <CallSentimentIcon :sentiment="call.sentiment" />
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.in_voicemail">
          <PropertyListItemLabel>
            <span class="flex items-center gap-1">
              Voicemail
              <Tooltip>
                <TooltipTrigger as-child>
                  <i class="fas fa-circle-info text-muted-foreground text-xs cursor-default" />
                </TooltipTrigger>
                <TooltipContent>
                  Detection is based on transcript analysis and may not be accurate for non-English
                  calls.
                </TooltipContent>
              </Tooltip>
            </span>
          </PropertyListItemLabel>
          <PropertyListItemValue>
            <i
              :class="
                cn(
                  'fas',
                  call.disconnection_reason === 'voicemail_left'
                    ? 'fa-check text-success'
                    : 'fa-close text-muted-foreground',
                )
              "
            />
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem>
          <PropertyListItemLabel> Classifications</PropertyListItemLabel>
          <PropertyListItemValue>
            {{
              call.classifications?.length
                ? call.classifications
                    .map((ic) => changeCase.capitalCase(ic.classification))
                    .join(', ')
                : '--'
            }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.custom_classifications?.length">
          <PropertyListItemLabel> Custom Classifications</PropertyListItemLabel>
          <PropertyListItemValue>
            {{ call.custom_classifications.join(', ') }}
          </PropertyListItemValue>
        </PropertyListItem>

        <PropertyListItem v-if="call.direction === Direction.OUTBOUND">
          <PropertyListItemLabel> Requested By</PropertyListItemLabel>
          <PropertyListItemValue>
            <span>
              {{ requestedBy }}
            </span>
          </PropertyListItemValue>
        </PropertyListItem>
      </PropertyList>

      <Separator v-if="call.summary" />

      <Section v-if="call.summary">
        <SectionHeader>
          <SectionTitle> Summary</SectionTitle>
        </SectionHeader>

        <SectionContent>
          <p>{{ call.summary }}</p>
        </SectionContent>
      </Section>
    </CardContent>
  </Card>
</template>

<style scoped lang="postcss">
  @reference "../../globals.css";

  .property-list-item {
    @apply grid-cols-[200px_1fr]!;
  }
</style>
